/**
 * OpsRegionalDrilldown — shared Region → City → Property drill-down
 * shell used across every Operations tab (Violations, Daily Reports,
 * Fleet Safety, Swaps, QR Codes, Weekly, All Reports, Uber Trash,
 * Live Tracking, Live Chats, Properties, Training Completions,
 * Demo Tools).
 *
 * Every tab passes its own `items` list and a `renderItems(items,
 * property)` function. The shell handles the cascading Region → City
 * → Property navigation, count badges at every level, and a
 * consistent detail panel where the tab-specific info is rendered.
 *
 * Rendered layout at 1280+ px:
 *   [Region rail] [City rail] [Property rail]  [Detail panel]
 * Below the panel: the property's service package + active specialty
 * services (auto-detected from the property doc), so Managers &
 * Supervisors always see the operational context before assigning
 * crews or drilling into tab-specific info.
 */
import { useMemo, useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  MapPin, Building2, Home, Hash, ChevronRight, Package, Sparkles,
  Flag, Calendar, User, Image as ImageIcon,
} from 'lucide-react';
import { US_STATES, normalizeState } from '../../lib/usStates';

const PACKAGE_LABEL = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
  custom: 'Custom',
};

function propertyPackageLabel(p) {
  const code = p?.service_package || p?.package_type || p?.package || p?.tier;
  if (!code) return null;
  const k = String(code).toLowerCase();
  return PACKAGE_LABEL[k] || String(code);
}

// Read specialty services stored on the property (property.service_policies
// is a dict keyed by service_type). Returns array of {type,label}.
const SPECIALTY_LABELS = {
  bulk_removal: 'Bulk Removal',
  pressure_washing: 'Pressure Washing',
  window_cleaning: 'Window Cleaning',
  dumpster_treatment: 'Dumpster Treatment',
  trash_room_maintenance: 'Trash Room Maintenance',
  eco_friendly_amenities: 'Eco-Friendly Amenities',
};
function propertySpecialtyServices(p) {
  const pol = p?.service_policies || {};
  return Object.keys(pol)
    .filter((k) => pol[k] && (pol[k].enabled === undefined || pol[k].enabled))
    .map((k) => ({ type: k, label: SPECIALTY_LABELS[k] || k }));
}

// Best-effort extraction of image URLs from an arbitrary item shape.
// Handles common NECS fields: photos[], photo_urls[], vehicle_photos[],
// license_photo, receipt_photo, before/after_photo, image_url, thumbnail.
// The NECS backend stores per-photo objects as {data: 'data:image/...'}
// (see backend/models.py photos: List[dict]) so we accept that shape too.
function extractItemImages(item) {
  if (!item || typeof item !== 'object') return [];
  const out = [];
  const isUrlish = (s) => typeof s === 'string' && /^(https?:|data:|\/api\/|\/)/.test(s);
  const push = (v) => {
    if (!v) return;
    if (typeof v === 'string') {
      if (isUrlish(v)) out.push(v);
    } else if (typeof v === 'object') {
      const candidate = v.url || v.data || v.src || v.base64 || v.thumbnail || v.href;
      if (isUrlish(candidate)) out.push(candidate);
    }
  };
  ['photo', 'photo_url', 'image', 'image_url', 'thumbnail',
   'license_photo', 'receipt_photo', 'gas_receipt_photo',
   'before_photo', 'after_photo', 'signature_url',
  ].forEach((k) => push(item[k]));
  ['photos', 'photo_urls', 'images', 'vehicle_photos', 'attachments'].forEach((k) => {
    const arr = item[k];
    if (Array.isArray(arr)) arr.forEach(push);
  });
  return out;
}

// Best-effort extraction of a display name for the agent involved in an item.
function extractItemAgent(item) {
  if (!item || typeof item !== 'object') return null;
  return (
    item.driver_name || item.agent_name || item.staff_name || item.user_name ||
    item.assigned_to_name || item.reporter_name || item.created_by_name ||
    item.driver_id || item.agent_id || item.staff_id || item.user_id || null
  );
}

// Coerce common timestamp shapes into millis-since-epoch.
function itemTimestamp(item) {
  if (!item) return 0;
  const raw = item.created_at || item.timestamp || item.date || item.reported_at || item.updated_at;
  if (!raw) return 0;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

// Fixed 5-tab strip of core TX metros used everywhere in the app.
// Extend when NECS expands geographically.
const BIG_CITY_TABS = ['Dallas', 'Houston', 'Austin', 'San Antonio', 'Tyler'];

export default function OpsRegionalDrilldown({
  regions = [],
  properties = [],
  items = [],
  getItemPropertyId = (i) => i?.property_id,
  sectionLabel = 'Section',
  sectionIcon: Icon = MapPin,
  renderItems, // (items, property) => ReactNode
  emptyItemsLabel = 'No records for this property yet.',
  headerExtras = null,
  testIdPrefix = 'ops-drilldown',
}) {
  const [selectedState, setSelectedState] = useState(null); // null → auto-select first available state
  // Big-City tab list (Dallas · Houston · Austin · San Antonio · Tyler).
  // Same 5-tab strip used across Routes & Regions and every Operations
  // drill-down so supervisors get one consistent metro switcher.
  const [selectedBigCity, setSelectedBigCity] = useState('Dallas');
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [agentFilter, setAgentFilter] = useState('__ALL__');

  // ── Date + agent filter applied to the raw items list ──────────────────
  const filteredItems = useMemo(() => {
    const fromMs = dateFrom ? new Date(dateFrom).getTime() : null;
    const toMs = dateTo ? new Date(dateTo).getTime() + 24 * 60 * 60 * 1000 - 1 : null;
    return (items || []).filter((it) => {
      if (agentFilter !== '__ALL__') {
        const a = extractItemAgent(it);
        if ((a || 'Unknown') !== agentFilter) return false;
      }
      if (fromMs || toMs) {
        const ts = itemTimestamp(it);
        if (!ts) return false;
        if (fromMs && ts < fromMs) return false;
        if (toMs && ts > toMs) return false;
      }
      return true;
    });
  }, [items, dateFrom, dateTo, agentFilter]);

  // Every derived aggregation below runs on filteredItems, not raw items.
  const activeItems = filteredItems;

  // ── Aggregate items by property_id for count badges ────────────────────
  const itemsByProperty = useMemo(() => {
    const m = {};
    (activeItems || []).forEach((it) => {
      const pid = getItemPropertyId(it);
      if (!pid) return;
      m[pid] = m[pid] || [];
      m[pid].push(it);
    });
    return m;
  }, [activeItems, getItemPropertyId]);

  // ── Property counts per city / region ─────────────────────────────────
  const { propsByCity, propsByRegion, propsByRegionCity } = useMemo(() => {
    const city = {};
    const region = {};
    const regionCity = {};
    (properties || []).forEach((p) => {
      const cKey = (p.city || '').trim().toLowerCase();
      if (cKey) city[cKey] = (city[cKey] || 0) + 1;
      if (p.region_id) {
        region[p.region_id] = (region[p.region_id] || 0) + 1;
        regionCity[p.region_id] = regionCity[p.region_id] || {};
        if (cKey) {
          regionCity[p.region_id][cKey] = regionCity[p.region_id][cKey] || [];
          regionCity[p.region_id][cKey].push(p);
        }
      }
    });
    return { propsByCity: city, propsByRegion: region, propsByRegionCity: regionCity };
  }, [properties]);

  const itemsByRegion = useMemo(() => {
    const m = {};
    (properties || []).forEach((p) => {
      const c = (itemsByProperty[p.property_id] || []).length;
      if (p.region_id && c) m[p.region_id] = (m[p.region_id] || 0) + c;
    });
    return m;
  }, [properties, itemsByProperty]);

  const itemsByCity = useMemo(() => {
    const m = {};
    (properties || []).forEach((p) => {
      const c = (itemsByProperty[p.property_id] || []).length;
      const cKey = (p.city || '').trim().toLowerCase();
      if (cKey && c) m[cKey] = (m[cKey] || 0) + c;
    });
    return m;
  }, [properties, itemsByProperty]);

  // 2-letter USPS code helper (delegates to the shared /lib/usStates catalog).
  const normState = (v) => normalizeState(v) || 'TX';

  // ── State selector shows the FULL US catalog per CEO directive (Feb 2026):
  // every state is selectable, even ones without seeded regions yet, so the
  // drilldown is future-proof as NECS expands geographically.
  const availableStates = US_STATES;
  // Set of states that currently have at least one seeded region — used to
  // put a subtle badge next to the state name so the user knows which ones
  // actually resolve to something today.
  const seededStateSet = useMemo(() => {
    const s = new Set();
    (regions || []).forEach((r) => { if (r.state) s.add(normState(r.state)); });
    return s;
  }, [regions]);

  const usableRegions = useMemo(
    () => (regions || [])
      .filter((r) => (r.cities || []).length > 0)
      .filter((r) => !selectedState || selectedState === '__ALL__' || normState(r.state) === selectedState)
      .filter((r) => !selectedBigCity || selectedBigCity === '__ALL__' || (r.big_city || 'Unassigned') === selectedBigCity)
      .slice()
      .sort((a, b) => (a.region_code || 'zzz').localeCompare(b.region_code || 'zzz')),
    [regions, selectedState, selectedBigCity],
  );

  // Big-City catalog for the picked state (per CEO Feb 2026 cascade).
  const availableBigCities = useMemo(() => {
    const seen = new Set();
    (regions || []).forEach((r) => {
      if (selectedState && selectedState !== '__ALL__' && normState(r.state) !== selectedState) return;
      seen.add(r.big_city || 'Unassigned');
    });
    const preferred = ['Dallas', 'Austin', 'Houston', 'San Antonio', 'Tyler'];
    return [
      ...preferred.filter((c) => seen.has(c)),
      ...Array.from(seen).filter((c) => !preferred.includes(c)).sort(),
    ];
  }, [regions, selectedState]);

  // ── Available agents in the (unfiltered) items list ──────────────────
  const availableAgents = useMemo(() => {
    const s = new Set();
    (items || []).forEach((it) => {
      const a = extractItemAgent(it);
      if (a) s.add(a);
    });
    return Array.from(s).sort();
  }, [items]);

  const selectedRegion = useMemo(
    () => usableRegions.find((r) => r.region_id === selectedRegionId) || null,
    [usableRegions, selectedRegionId],
  );

  // When state changes, clear region + big-city selection so cascade recomputes.
  useEffect(() => {
    setSelectedRegionId((prev) => (prev === null ? prev : null));
  }, [selectedState]);

  // When big city changes, clear region selection so auto-select recomputes.
  useEffect(() => {
    setSelectedRegionId((prev) => (prev === null ? prev : null));
  }, [selectedBigCity]);

  // Auto-pick a sensible default state on mount so regions are populated
  // immediately (per CEO directive: don't default to "All states"). We
  // prefer the first *seeded* state (one with regions), falling back to
  // the alphabetical first of the full US catalog.
  useEffect(() => {
    if (selectedState !== null) return;
    if (availableStates.length === 0) return;
    const firstSeeded = availableStates.find((s) => seededStateSet.has(s));
    setSelectedState(firstSeeded || availableStates[0]);
  }, [selectedState, availableStates, seededStateSet]);

  // Auto-select first region with items > 0, else first region with properties, else the first.
  useEffect(() => {
    if (selectedRegionId || usableRegions.length === 0) return;
    const withItems = usableRegions.find((r) => (itemsByRegion[r.region_id] || 0) > 0);
    const withProps = usableRegions.find((r) => (propsByRegion[r.region_id] || 0) > 0);
    const next = (withItems || withProps || usableRegions[0]).region_id;
    setSelectedRegionId((prev) => (prev === next ? prev : next));
  }, [usableRegions, itemsByRegion, propsByRegion, selectedRegionId]);

  // On region change, pick the first city that has items (else first with props, else first).
  // Deps use only the region_id primitive to avoid useMemo identity cycles.
  const selectedRegionKey = selectedRegion?.region_id || null;
  useEffect(() => {
    if (!selectedRegion) {
      setSelectedCity((prev) => (prev === null ? prev : null));
      return;
    }
    const cities = selectedRegion.cities || [];
    if (cities.length === 0) {
      setSelectedCity((prev) => (prev === null ? prev : null));
      return;
    }
    const cityWithItems = cities.find((c) => (itemsByCity[c.trim().toLowerCase()] || 0) > 0);
    const cityWithProps = cities.find((c) => (propsByCity[c.trim().toLowerCase()] || 0) > 0);
    const next = cityWithItems || cityWithProps || cities[0];
    setSelectedCity((prev) => (prev === next ? prev : next));
    setSelectedPropertyId((prev) => (prev === null ? prev : null));
  }, [selectedRegionKey]);

  const propertiesForCity = useMemo(() => {
    if (!selectedRegion || !selectedCity) return [];
    const cKey = selectedCity.trim().toLowerCase();
    const primary = (propsByRegionCity[selectedRegion.region_id] || {})[cKey] || [];
    const unregioned = (properties || []).filter(
      (p) => (p.city || '').trim().toLowerCase() === cKey && !p.region_id,
    );
    const merged = [...primary];
    const seen = new Set(primary.map((p) => p.property_id));
    unregioned.forEach((p) => { if (!seen.has(p.property_id)) merged.push(p); });
    return merged;
  }, [selectedRegion, selectedCity, propsByRegionCity, properties]);

  // Auto-select first property with items, else first. Guard against
  // memo-identity cycles by depending on primitive keys only.
  const propertiesForCityKey = propertiesForCity.map((p) => p.property_id).join(',');
  useEffect(() => {
    if (propertiesForCity.length === 0) {
      setSelectedPropertyId((prev) => (prev === null ? prev : null));
      return;
    }
    if (selectedPropertyId && propertiesForCity.some((p) => p.property_id === selectedPropertyId)) return;
    const withItems = propertiesForCity.find((p) => (itemsByProperty[p.property_id] || []).length > 0);
    const next = (withItems || propertiesForCity[0]).property_id;
    setSelectedPropertyId((prev) => (prev === next ? prev : next));
  }, [propertiesForCityKey]);

  const selectedProperty = useMemo(
    () => propertiesForCity.find((p) => p.property_id === selectedPropertyId) || null,
    [propertiesForCity, selectedPropertyId],
  );

  const detailItems = useMemo(
    () => selectedProperty ? (itemsByProperty[selectedProperty.property_id] || []) : [],
    [selectedProperty, itemsByProperty],
  );

  const totalItems = filteredItems?.length || 0;
  const galleryUrls = useMemo(() => {
    const urls = [];
    detailItems.forEach((it) => extractItemImages(it).forEach((u) => urls.push(u)));
    return urls;
  }, [detailItems]);

  return (
    <Card
      className="bg-zinc-900 border-2 border-[#FFD60A]/40 p-4"
      data-testid={`${testIdPrefix}-shell`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3
            className="text-lg font-bold text-white flex items-center gap-2"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            <Icon className="h-5 w-5 text-[#FFD60A]" />
            {(selectedBigCity || '').toUpperCase()} · {sectionLabel.toUpperCase()} · REGIONAL DRILL-DOWN
          </h3>
          <p className="text-[11px] text-zinc-400 mt-0.5 max-w-2xl">
            {selectedBigCity ? (
              <>Showing only the <strong className="text-[#C9A962]">{selectedBigCity}</strong> metro. Navigate <strong className="text-[#C9A962]">Region → City → Property</strong>. The right panel shows this section&apos;s records for the selected property alongside its service package &amp; active specialty services.</>
            ) : (
              <>Navigate <strong className="text-[#C9A962]">Region → City → Property</strong>. The right panel shows this section&apos;s records for the selected property alongside its service package &amp; active specialty services.</>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge className="bg-emerald-700 text-white" data-testid={`${testIdPrefix}-total`}>
            {totalItems} shown{items.length !== totalItems ? ` · ${items.length} total` : ''}
          </Badge>
          {headerExtras}
        </div>
      </div>

      {/* ── Big-City tab strip (primary metro selector, matches Routes
          & Regions style). Consistent across every Operations
          drill-down so supervisors always find the same switcher. */}
      <div
        className="flex items-center justify-between gap-3 flex-wrap mb-3 pb-3 border-b border-zinc-800"
        data-testid={`${testIdPrefix}-bigcity-tabs`}
      >
        <div className="flex items-center gap-1.5 flex-wrap">
          {BIG_CITY_TABS.map((city) => {
            const active = selectedBigCity === city;
            return (
              <button
                key={city}
                type="button"
                onClick={() => setSelectedBigCity(city)}
                className={`px-3.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all border ${
                  active
                    ? 'bg-[#FFD60A] text-black border-[#FFD60A] shadow-[0_0_0_1px_#FFD60A]'
                    : 'bg-zinc-950 text-zinc-300 border-zinc-700 hover:border-[#C9A962] hover:text-[#C9A962]'
                }`}
                data-testid={`${testIdPrefix}-bigcity-tab-${city.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {city}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">State</span>
          <Select value={selectedState || '__ALL__'} onValueChange={setSelectedState}>
            <SelectTrigger
              className="h-7 w-[100px] bg-zinc-950 border-zinc-700 text-zinc-300 text-[11px]"
              data-testid={`${testIdPrefix}-state-select`}
            >
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-72">
              <SelectItem value="__ALL__">All</SelectItem>
              {availableStates.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                  {seededStateSet.has(s) && ' ·'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Filter row · Date range + Agent (State & Big-City live above) ─── */}
      <div
        className="flex flex-wrap items-end gap-2 mb-3 pb-3 border-b border-zinc-800"
        data-testid={`${testIdPrefix}-filters`}
      >
        <div className="flex flex-col gap-1">
          <label className="text-[9px] uppercase tracking-widest text-[#C9A962] font-bold flex items-center gap-1">
            <Calendar className="h-2.5 w-2.5" /> From
          </label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-8 w-[135px] bg-zinc-950 border-zinc-700 text-white text-[11px]"
            data-testid={`${testIdPrefix}-date-from`}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[9px] uppercase tracking-widest text-[#C9A962] font-bold flex items-center gap-1">
            <Calendar className="h-2.5 w-2.5" /> To
          </label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-8 w-[135px] bg-zinc-950 border-zinc-700 text-white text-[11px]"
            data-testid={`${testIdPrefix}-date-to`}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[9px] uppercase tracking-widest text-[#C9A962] font-bold flex items-center gap-1">
            <User className="h-2.5 w-2.5" /> Agent
          </label>
          <Select value={agentFilter} onValueChange={setAgentFilter}>
            <SelectTrigger
              className="h-8 w-[160px] bg-zinc-950 border-zinc-700 text-white text-[11px]"
              data-testid={`${testIdPrefix}-agent-select`}
            >
              <SelectValue placeholder="All agents" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-60">
              <SelectItem value="__ALL__">All agents</SelectItem>
              {availableAgents.map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
              {availableAgents.length === 0 && (
                <div className="px-2 py-1 text-[11px] text-zinc-500 italic">
                  No agents on file
                </div>
              )}
            </SelectContent>
          </Select>
        </div>
        {(dateFrom || dateTo || agentFilter !== '__ALL__') && (
          <button
            type="button"
            onClick={() => {
              setDateFrom(''); setDateTo('');
              setAgentFilter('__ALL__');
            }}
            className="h-8 px-3 rounded text-[11px] bg-zinc-800 text-zinc-300 hover:bg-zinc-700 border border-zinc-700"
            data-testid={`${testIdPrefix}-clear-filters`}
          >
            Clear
          </button>
        )}
      </div>

      {/* 4-column layout */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
        {/* Column 1 · Regions */}
        <div className="xl:col-span-2">
          <ColumnHeader icon={MapPin} label="Regions" />
          <ul
            className="space-y-1 max-h-[420px] overflow-y-auto pr-1"
            data-testid={`${testIdPrefix}-region-list`}
          >
            {usableRegions.map((r) => {
              const active = r.region_id === selectedRegionId;
              const propCount = propsByRegion[r.region_id] || 0;
              const itemCount = itemsByRegion[r.region_id] || 0;
              return (
                <li key={r.region_id}>
                  <button
                    type="button"
                    onClick={() => setSelectedRegionId(r.region_id)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded border ${
                      active
                        ? 'bg-[#FFD60A]/10 border-[#FFD60A]'
                        : 'bg-zinc-950/40 border-zinc-800 hover:bg-zinc-900'
                    }`}
                    data-testid={`${testIdPrefix}-region-${r.region_code || r.region_id}`}
                  >
                    <span
                      className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: r.color || '#C9A962' }}
                    />
                    <span className="flex-1 min-w-0 text-left">
                      <span className="block text-[12px] text-white font-semibold truncate">
                        <span className="font-mono text-[#C9A962] mr-1">{r.region_code || '—'}</span>
                        {r.name}
                      </span>
                      <span className="block text-[9px] text-zinc-500">
                        {propCount} prop
                      </span>
                    </span>
                    <Badge
                      className={`text-[9px] font-bold px-1.5 ${
                        itemCount > 0 ? 'bg-emerald-700 text-white' : 'bg-zinc-800 text-zinc-500'
                      }`}
                      data-testid={`${testIdPrefix}-region-count-${r.region_code || r.region_id}`}
                    >
                      {itemCount}
                    </Badge>
                  </button>
                </li>
              );
            })}
            {usableRegions.length === 0 && (
              <li className="text-[11px] text-zinc-500 text-center py-4">
                Seed the DFW catalog first.
              </li>
            )}
          </ul>
        </div>

        {/* Column 2 · Cities */}
        <div className="xl:col-span-3">
          <ColumnHeader icon={Home} label={selectedRegion ? `Cities · ${selectedRegion.region_code}` : 'Cities'} />
          <ul
            className="space-y-1 max-h-[420px] overflow-y-auto pr-1"
            data-testid={`${testIdPrefix}-city-list`}
          >
            {selectedRegion && (selectedRegion.cities || []).map((city, idx) => {
              const cKey = city.trim().toLowerCase();
              const active = selectedCity === city;
              const propCount = propsByCity[cKey] || 0;
              const itemCount = itemsByCity[cKey] || 0;
              return (
                <li key={city}>
                  <button
                    type="button"
                    onClick={() => { setSelectedCity(city); setSelectedPropertyId(null); }}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded border ${
                      active
                        ? 'bg-[#FFD60A]/10 border-[#FFD60A]'
                        : 'bg-zinc-950/40 border-zinc-800 hover:bg-zinc-900'
                    }`}
                    data-testid={`${testIdPrefix}-city-${selectedRegion.region_code}-${idx + 1}`}
                  >
                    <span className="text-[10px] font-mono text-[#C9A962] font-bold min-w-[26px] text-left">
                      <Hash className="h-3 w-3 inline mr-0.5" />{idx + 1}
                    </span>
                    <span className="text-[12px] text-white flex-1 text-left truncate">{city}</span>
                    <Badge
                      className={`text-[9px] font-bold px-1.5 ${
                        propCount > 0 ? 'bg-zinc-700 text-white' : 'bg-zinc-800 text-zinc-500'
                      }`}
                    >
                      <Building2 className="h-2.5 w-2.5 mr-0.5" />{propCount}
                    </Badge>
                    {itemCount > 0 && (
                      <Badge className="text-[9px] font-bold px-1.5 bg-emerald-700 text-white">
                        {itemCount}
                      </Badge>
                    )}
                  </button>
                </li>
              );
            })}
            {!selectedRegion && (
              <li className="text-[11px] text-zinc-500 text-center py-4">
                ← Select a region.
              </li>
            )}
          </ul>
        </div>

        {/* Column 3 · Properties */}
        <div className="xl:col-span-3">
          <ColumnHeader icon={Building2} label={selectedCity ? `Properties · ${selectedCity}` : 'Properties'} />
          <ul
            className="space-y-1 max-h-[420px] overflow-y-auto pr-1"
            data-testid={`${testIdPrefix}-property-list`}
          >
            {propertiesForCity.map((p) => {
              const active = p.property_id === selectedPropertyId;
              const itemCount = (itemsByProperty[p.property_id] || []).length;
              return (
                <li key={p.property_id}>
                  <button
                    type="button"
                    onClick={() => setSelectedPropertyId(p.property_id)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded border text-left ${
                      active
                        ? 'bg-[#FFD60A]/10 border-[#FFD60A]'
                        : 'bg-zinc-950/40 border-zinc-800 hover:bg-zinc-900'
                    }`}
                    data-testid={`${testIdPrefix}-prop-${p.property_id}`}
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block text-[12px] text-white font-semibold truncate">
                        {p.property_name || p.name || 'Unnamed'}
                      </span>
                      <span className="block text-[9px] text-zinc-500 truncate">
                        {p.address || '—'}
                      </span>
                    </span>
                    {itemCount > 0 ? (
                      <Badge className="text-[9px] font-bold px-1.5 bg-emerald-700 text-white">
                        {itemCount}
                      </Badge>
                    ) : (
                      <Badge className="text-[9px] font-bold px-1.5 bg-zinc-800 text-zinc-500">
                        0
                      </Badge>
                    )}
                    {active && <ChevronRight className="h-3 w-3 text-[#FFD60A]" />}
                  </button>
                </li>
              );
            })}
            {propertiesForCity.length === 0 && selectedCity && (
              <li className="text-[11px] text-zinc-500 text-center py-4">
                No properties in {selectedCity}.
              </li>
            )}
          </ul>
        </div>

        {/* Column 4 · Detail panel */}
        <div className="xl:col-span-4">
          <ColumnHeader
            icon={Icon}
            label={selectedProperty ? sectionLabel : `${sectionLabel} · Detail`}
          />
          <div
            className="border border-zinc-800 rounded bg-zinc-950/40 p-3 min-h-[380px] max-h-[420px] overflow-y-auto"
            data-testid={`${testIdPrefix}-detail`}
          >
            {selectedProperty ? (
              <>
                <PropertyContextBlock property={selectedProperty} />
                <div className="mt-3">
                  {detailItems.length === 0 ? (
                    <p
                      className="text-[11px] text-zinc-500 italic text-center py-4"
                      data-testid={`${testIdPrefix}-detail-empty`}
                    >
                      {emptyItemsLabel}
                    </p>
                  ) : renderItems ? (
                    renderItems(detailItems, selectedProperty)
                  ) : (
                    <ul className="text-xs text-zinc-300 space-y-1">
                      {detailItems.map((it, i) => (
                        <li key={i} className="border border-zinc-800 rounded p-1.5">
                          {typeof it === 'string' ? it : JSON.stringify(it).slice(0, 100)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {galleryUrls.length > 0 && (
                  <GalleryPanel
                    urls={galleryUrls}
                    testIdPrefix={testIdPrefix}
                  />
                )}
              </>
            ) : (
              <p className="text-[11px] text-zinc-500 italic text-center py-4">
                ← Pick a region → city → property.
              </p>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function ColumnHeader({ icon: Icon, label }) {
  return (
    <div className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold mb-2 flex items-center gap-1">
      <Icon className="h-3 w-3" /> {label}
    </div>
  );
}

/** Property context block — package + specialty services + region.
 *  Rendered above every tab-specific detail so Managers/Supervisors
 *  see operational context in one place. */
function PropertyContextBlock({ property: p }) {
  const pkg = propertyPackageLabel(p);
  const specials = propertySpecialtyServices(p);
  return (
    <div
      className="border border-[#C9A962]/30 bg-[#0f0f0f] rounded p-2.5 space-y-1.5"
      data-testid="ops-drilldown-property-context"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] text-white font-bold truncate">
            {p.property_name || p.name}
          </div>
          <div className="text-[10px] text-zinc-400 truncate">
            {p.address || '—'} · {p.city || '—'}
          </div>
        </div>
        <Badge className="text-[9px] font-bold bg-zinc-800 text-[#C9A962] font-mono flex-shrink-0">
          {p.region_code || '—'}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <span
          className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] ${
            pkg ? 'border-[#C9A962]/40 bg-[#C9A962]/10 text-[#C9A962]' : 'border-zinc-700 text-zinc-500'
          }`}
          data-testid="ops-drilldown-package"
        >
          <Package className="h-2.5 w-2.5" />
          {pkg || 'No package'}
        </span>
        {specials.length === 0 ? (
          <span className="inline-flex items-center gap-1 rounded border border-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-500">
            <Sparkles className="h-2.5 w-2.5" />
            No specialty services
          </span>
        ) : (
          specials.map((s) => (
            <span
              key={s.type}
              className="inline-flex items-center gap-1 rounded border border-purple-500/40 bg-purple-500/10 text-purple-300 px-1.5 py-0.5 text-[10px]"
              data-testid={`ops-drilldown-specialty-${s.type}`}
            >
              <Sparkles className="h-2.5 w-2.5" />
              {s.label}
            </span>
          ))
        )}
      </div>
      {p.has_signed_contract && (
        <div className="text-[10px] text-emerald-400">
          ✓ Signed contract on file
        </div>
      )}
    </div>
  );
}


/** GalleryPanel — thumbnails of every image URL found in the currently
 *  filtered items for the selected property. Clicking a thumbnail opens
 *  the full-size asset in a new tab. */
function GalleryPanel({ urls, testIdPrefix }) {
  if (!urls || urls.length === 0) return null;
  return (
    <div
      className="mt-3 border-t border-zinc-800 pt-2"
      data-testid={`${testIdPrefix}-gallery`}
    >
      <div className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold mb-1.5 flex items-center gap-1">
        <ImageIcon className="h-3 w-3" />
        Gallery · {urls.length} photo{urls.length === 1 ? '' : 's'}
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {urls.slice(0, 12).map((u, i) => (
          <a
            key={`${u}-${i}`}
            href={u}
            target="_blank"
            rel="noreferrer"
            className="block aspect-square bg-zinc-900 rounded overflow-hidden border border-zinc-800 hover:border-[#FFD60A] transition-colors"
            data-testid={`${testIdPrefix}-gallery-thumb-${i}`}
          >
            <img
              src={u}
              alt={`Item ${i + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
              loading="lazy"
            />
          </a>
        ))}
        {urls.length > 12 && (
          <div className="col-span-4 text-[10px] text-zinc-500 italic text-center pt-1">
            +{urls.length - 12} more
          </div>
        )}
      </div>
    </div>
  );
}
